import type { JSONContent } from '@sequencehq/core-components'
import type {
  EnrichedText,
  QuoteVariableType,
  TextBlockConfig
} from '@sequencehq/quote-content'
import { dequal } from 'dequal'
import { match } from 'ts-pattern'

type Mark = NonNullable<JSONContent['marks']>[number]

const STYLE_MARKS = ['bold', 'italic', 'underline', 'strike'] as const

type StyleMark = (typeof STYLE_MARKS)[number]

const COLOR_MARKS = ['textColor', 'backgroundColor'] as const

type ColorMark = (typeof COLOR_MARKS)[number]

/**
 * Converts between saved text and the inline content of a text block. A `\n`
 * in the saved text is a hard break in the editor. Hrefs are saved as they
 * are. New links are normalised when they're made, so loaded ones come back
 * unchanged rather than rewritten.
 */
export const textContentAdapter = {
  in: (text: TextBlockConfig['text']): JSONContent[] =>
    toEnrichedText(text).reduce<JSONContent[]>((acc, item) => {
      if (!item.variable) {
        return [...acc, ...textToNodes(item)]
      }

      const previous = acc.at(-1)
      const continuesPreviousVariable =
        previous?.type === 'inlineVariable' &&
        previous.attrs?.variableType === item.variable

      /**
       * BlockNote could save a variable as several runs, one per style. The
       * editor holds a variable as one node, so the runs are joined, and the
       * first run's styles apply to all of it. Two of the same variable side by
       * side are joined too, as BlockNote did.
       */
      if (continuesPreviousVariable) {
        return [
          ...acc.slice(0, -1),
          {
            ...previous,
            attrs: { ...previous.attrs, text: previous.attrs?.text + item.text }
          }
        ]
      }

      const marks = toMarks(item)

      return [
        ...acc,
        {
          type: 'inlineVariable',
          attrs: { variableType: item.variable, text: item.text },
          ...(marks.length > 0 && { marks })
        }
      ]
    }, []),
  out: (content: JSONContent[] = []): EnrichedText[] =>
    content.reduce<EnrichedText[]>((acc, node) => {
      const previous = acc.at(-1)

      return (
        match(node.type)
          .with('text', () => {
            const href = toHref(node.marks)
            const item = {
              styles: toStyles(node.marks),
              text: node.text ?? '',
              ...(href !== undefined && { href })
            }

            if (
              previous &&
              previous.variable === undefined &&
              dequal(previous.styles, item.styles) &&
              previous.href === item.href
            ) {
              return [
                ...acc.slice(0, -1),
                { ...previous, text: previous.text + item.text }
              ]
            }

            return [...acc, item]
          })
          /**
           * BlockNote added a line break to the end of the run before it, whatever
           * its styles, so we do the same to save identical text. A variable's
           * text is its value, so the break starts a new run after it instead.
           */
          .with('hardBreak', () =>
            previous && previous.variable === undefined
              ? [
                  ...acc.slice(0, -1),
                  { ...previous, text: `${previous.text}\n` }
                ]
              : [...acc, { styles: {}, text: '\n' }]
          )
          /**
           * The text is saved as the value was produced, including the
           * invisible padding around email addresses. A variable with no text
           * has nothing to show, so it's dropped, as BlockNote did.
           */
          .with('inlineVariable', () => {
            const text: string = node.attrs?.text ?? ''
            const href = toHref(node.marks)

            if (!text) {
              return acc
            }

            return [
              ...acc,
              {
                styles: toStyles(node.marks),
                text,
                variable: node.attrs?.variableType as QuoteVariableType,
                ...(href !== undefined && { href })
              }
            ]
          })
          .otherwise(() => acc)
      )
    }, [])
}

const toEnrichedText = (text: TextBlockConfig['text']): EnrichedText[] => {
  if (typeof text === 'string') {
    return [{ styles: {}, text }]
  }

  return (text as Array<string | null | EnrichedText>).map(item =>
    typeof item === 'string' || item === null
      ? { styles: {}, text: item ?? '' }
      : { ...item, styles: item.styles ?? {} }
  )
}

const textToNodes = (item: EnrichedText): JSONContent[] => {
  const marks = toMarks(item)

  return item.text
    .split('\n')
    .flatMap((line, index) => [
      ...(index > 0 ? [{ type: 'hardBreak' }] : []),
      ...(line
        ? [{ type: 'text', text: line, ...(marks.length > 0 && { marks }) }]
        : [])
    ])
}

/**
 * Colours are copied as they are, palette names included, so saved text comes
 * back unchanged.
 */
const toMarks = (item: EnrichedText): Mark[] => [
  ...(item.href ? [{ type: 'link', attrs: { href: item.href } }] : []),
  ...STYLE_MARKS.filter(style => item.styles[style]).map(type => ({ type })),
  ...COLOR_MARKS.flatMap(type => {
    const color = item.styles[type]

    return color === undefined ? [] : [{ type, attrs: { color } }]
  })
]

const toStyles = (marks: Mark[] = []): EnrichedText['styles'] =>
  marks.reduce<EnrichedText['styles']>(
    (styles, mark) =>
      match(mark.type)
        .when(isStyleMark, type => ({ ...styles, [type]: true }))
        .when(isColorMark, type => ({ ...styles, [type]: mark.attrs?.color }))
        .otherwise(() => styles),
    {}
  )

const toHref = (marks: Mark[] = []): string | undefined =>
  marks.find(mark => mark.type === 'link')?.attrs?.href

const isStyleMark = (type: string): type is StyleMark =>
  STYLE_MARKS.includes(type as StyleMark)

const isColorMark = (type: string): type is ColorMark =>
  COLOR_MARKS.includes(type as ColorMark)
